import React, { useEffect, useState } from 'react';
import { motion } from 'framer-motion';
import { Helmet } from 'react-helmet-async';
import { FiUsers, FiSearch, FiBarChart2, FiPlus, FiEye, FiTrash2, FiMessageSquare, FiSend, FiShoppingCart, FiCheckCircle, FiAlertTriangle, FiActivity, FiCalendar, FiFilter, FiKey, FiShield } from 'react-icons/fi';
import { useForm } from 'react-hook-form';
import api, { getAdminDashboard, getLeads, getVisits, setup2FA, verify2FAAdmin, check2FAStatus } from '../services/api';

const Admin = () => {
  const [activeTab, setActiveTab] = useState('dashboard');
  const [stats, setStats] = useState({ totalLeads: 0, totalUsers: 0, totalSales: 0 });
  const [leads, setLeads] = useState([]);
  const [sales, setSales] = useState([]);
  const [conversations, setConversations] = useState([]);
  const [selectedChat, setSelectedChat] = useState(null);
  const [replyText, setReplyText] = useState('');
  const [loading, setLoading] = useState(true);
  const [visitsData, setVisitsData] = useState({ stats: {}, visits: [] });
  const [visitsPeriod, setVisitsPeriod] = useState('all');
  const [show2FASetup, setShow2FASetup] = useState(false);
  const [qrCodeUrl, setQrCodeUrl] = useState('');
  const [secret, setSecret] = useState('');
  const [totpCode, setTotpCode] = useState('');
  const [twoFAEnabled, setTwoFAEnabled] = useState(false);

  
  const { register, handleSubmit, reset, formState: { errors } } = useForm();

  useEffect(() => {
    loadData();

    // Polling para o suporte
    let interval;
    if (activeTab === 'support') {
      interval = setInterval(async () => {
        try {
          const response = await api.get('/admin/support/conversations');
          setConversations(response.data);
          
          // Se houver um chat selecionado, atualizar ele também
          if (selectedChat) {
            const updatedChat = response.data.find(c => c.customerId === selectedChat.customerId);
            if (updatedChat) {
              setSelectedChat(updatedChat);
            }
          }
        } catch (error) {
          console.error('Erro no polling de suporte:', error);
        }
      }, 3000);
    }

    return () => {
      if (interval) clearInterval(interval);
    };
  }, [activeTab, selectedChat?.customerId]);

  // Recarregar visitas quando o período mudar
  useEffect(() => {
    if (activeTab === 'visits') {
      loadData();
    }
  }, [visitsPeriod, activeTab]);

  const loadData = async () => {
    setLoading(true);
    try {
      if (activeTab === 'dashboard') {
        const response = await getAdminDashboard();
        setStats({
          ...response.data.stats,
          totalSales: response.data.recentLeads?.filter(l => l.status === 'paid').length || 0 // Fallback stat
        });
        setLeads(response.data.recentLeads || []);
      } else if (activeTab === 'leads') {
        const response = await getLeads();
        setLeads(response.data || []);
      } else if (activeTab === 'sales') {
        const response = await api.get('/admin/sales');
        setSales(response.data);
      } else if (activeTab === 'support') {
        const response = await api.get('/admin/support/conversations');
        console.log('Conversas carregadas:', response.data);
        setConversations(response.data);
      } else if (activeTab === 'visits') {
        const response = await getVisits(visitsPeriod);
        setVisitsData(response.data);
      } else if (activeTab === 'security') {
        const response = await check2FAStatus();
        setTwoFAEnabled(response.data.enabled);
      }
    } catch (error) {
      console.error('Erro ao carregar dados:', error);
      // Fallback data if needed
    } finally {
      setLoading(false);
    }
  };

  const handleReply = async (e) => {
    e.preventDefault();
    if (!replyText.trim() || !selectedChat) return;

    try {
      await api.post('/admin/support/reply', {
        customerId: selectedChat.customerId,
        message: replyText
      });
      setReplyText('');
      // Recarregar conversas
      const response = await api.get('/admin/support/conversations');
      setConversations(response.data);
      // Atualizar chat selecionado
      const updatedChat = response.data.find(c => c.customerId === selectedChat.customerId);
      setSelectedChat(updatedChat);
    } catch (error) {
      console.error('Erro ao enviar resposta:', error);
    }
  };

  const handleDeleteMessage = async (customerId, messageId) => {
    if (!window.confirm('Deseja apagar esta mensagem (Staff)?')) return;

    try {
      await api.post('/admin/support/delete-message', { customerId, messageId });
      
      // Recarregar conversas para atualizar o estado
      const response = await api.get('/admin/support/conversations');
      setConversations(response.data);
      
      if (selectedChat?.customerId === customerId) {
        const updatedChat = response.data.find(c => c.customerId === customerId);
        setSelectedChat(updatedChat);
      }
    } catch (error) {
      console.error('Erro ao apagar mensagem:', error);
    }
  };

  const handleCloseChat = async (customerId) => {
    if (!window.confirm('Deseja realmente finalizar este atendimento?')) return;

    try {
      await api.post('/admin/support/close', { customerId });
      
      // Recarregar conversas
      const response = await api.get('/admin/support/conversations');
      setConversations(response.data);
      
      if (selectedChat?.customerId === customerId) {
        const updatedChat = response.data.find(c => c.customerId === customerId);
        setSelectedChat(updatedChat);
      }
    } catch (error) {
      console.error('Erro ao finalizar atendimento:', error);
    }
  };

  const formatDate = (date) => {
    return new Date(date).toLocaleDateString('pt-BR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const formatCPF = (cpf) => {
    if (!cpf) return '--';
    return `***.***.**${cpf.slice(-4)}`;
  };

  const formatCurrency = (value) => {
    return new Intl.NumberFormat('pt-BR', {
      style: 'currency',
      currency: 'BRL'
    }).format(value || 0);
  };

  const handleSetup2FA = async () => {
    try {
      const response = await setup2FA();
      setQrCodeUrl(response.data.qrCode);
      setSecret(response.data.secret);
      setShow2FASetup(true);
    } catch (err) {
      alert(err.response?.data?.error || 'Erro ao configurar 2FA');
    }
  };

  const handleVerify2FA = async () => {
    try {
      await verify2FAAdmin(totpCode);
      setTwoFAEnabled(true);
      setShow2FASetup(false);
      alert('2FA ativado com sucesso!');
    } catch (err) {
      alert(err.response?.data?.error || 'Código inválido');
    }
  };

  const tabs = [
    { id: 'dashboard', label: 'Dashboard', icon: FiBarChart2 },
    { id: 'visits', label: 'Visitas', icon: FiActivity },
    { id: 'leads', label: 'Leads', icon: FiUsers },
    { id: 'sales', label: 'Vendas', icon: FiShoppingCart },
    { id: 'support', label: 'Suporte Online', icon: FiMessageSquare },
    { id: 'security', label: 'Segurança', icon: FiKey },
  ];

  return (
    <>
      <Helmet>
        <title>Admin - ConsulteSeuNome</title>
      </Helmet>

      <div className="min-h-screen bg-gray-100">
        <div className="flex">
          {/* Sidebar */}
          <aside className="w-64 bg-white shadow-lg min-h-screen">
            <div className="p-6">
              <h2 className="text-xl font-bold text-gray-900">Admin Panel</h2>
              <p className="text-sm text-gray-500">ConsulteSeuNome</p>
            </div>
            <nav className="mt-4">
              {tabs.map((tab) => (
                <button
                  key={tab.id}
                  onClick={() => {
                    setActiveTab(tab.id);
                    loadData();
                  }}
                  className={`w-full flex items-center px-6 py-3 text-left transition-colors ${
                    activeTab === tab.id
                      ? 'bg-primary-50 text-primary-600 border-r-4 border-primary-600'
                      : 'text-gray-600 hover:bg-gray-50'
                  }`}
                >
                  <tab.icon className="w-5 h-5 mr-3" />
                  {tab.label}
                </button>
              ))}
            </nav>
          </aside>

          {/* Main Content */}
          <main className="flex-1 p-8">
            {activeTab === 'sales' && (
              <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
              >
                <h1 className="text-2xl font-bold text-gray-900 mb-8">Relatório de Vendas</h1>
                
                <div className="card">
                  <div className="overflow-x-auto">
                    <table className="w-full">
                      <thead>
                        <tr className="border-b border-gray-200">
                          <th className="text-left py-3 px-4 font-semibold text-gray-600">Cliente</th>
                          <th className="text-left py-3 px-4 font-semibold text-gray-600">Descrição</th>
                          <th className="text-left py-3 px-4 font-semibold text-gray-600">Valor</th>
                          <th className="text-left py-3 px-4 font-semibold text-gray-600">Método</th>
                          <th className="text-left py-3 px-4 font-semibold text-gray-600">Data</th>
                        </tr>
                      </thead>
                      <tbody>
                        {sales.length === 0 ? (
                          <tr>
                            <td colSpan="5" className="py-8 text-center text-gray-500">
                              Nenhuma venda realizada ainda.
                            </td>
                          </tr>
                        ) : (
                          sales.map((sale) => (
                            <tr key={sale.id} className="border-b border-gray-100 hover:bg-gray-50">
                              <td className="py-3 px-4">
                                <div className="font-medium text-gray-900">{sale.nome}</div>
                                <div className="text-xs text-gray-500">{sale.email}</div>
                              </td>
                              <td className="py-3 px-4 text-sm text-gray-600">{sale.descricao}</td>
                              <td className="py-3 px-4 font-bold text-success-600">{formatCurrency(sale.valor)}</td>
                              <td className="py-3 px-4">
                                <span className="px-2 py-1 rounded-full text-[10px] font-bold uppercase bg-blue-100 text-blue-600">
                                  {sale.metodo_pagamento}
                                </span>
                              </td>
                              <td className="py-3 px-4 text-gray-500 text-sm">{formatDate(sale.created_at)}</td>
                            </tr>
                          ))
                        )}
                      </tbody>
                    </table>
                  </div>
                </div>
              </motion.div>
            )}

            {activeTab === 'support' && (
              <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                className="h-[calc(100vh-120px)] flex flex-col"
              >
                <h1 className="text-2xl font-bold text-gray-900 mb-6">Suporte Online</h1>
                
                <div className="flex-1 flex gap-6 overflow-hidden">
                  {/* Conversations List */}
                  <div className="w-1/3 bg-white rounded-2xl shadow-sm border border-gray-100 flex flex-col overflow-hidden">
                    <div className="p-4 border-b border-gray-100 bg-gray-50/50">
                      <h3 className="font-bold text-gray-700">Conversas Ativas</h3>
                    </div>
                    <div className="flex-1 overflow-y-auto">
                      {conversations.length === 0 ? (
                        <div className="p-8 text-center text-gray-500">
                          <FiMessageSquare className="w-12 h-12 mx-auto mb-3 opacity-20" />
                          <p>Nenhuma conversa ativa no momento.</p>
                        </div>
                      ) : (
                        conversations.map((chat) => (
                          <button
                            key={chat.customerId}
                            onClick={() => setSelectedChat(chat)}
                            className={`w-full p-4 text-left border-b border-gray-50 transition-colors hover:bg-gray-50 ${
                              selectedChat?.customerId === chat.customerId ? 'bg-primary-50 border-r-4 border-primary-600' : ''
                            }`}
                          >
                            <div className="flex justify-between items-start mb-1">
                              <span className="font-bold text-gray-900 truncate">{chat.customerName}</span>
                              <span className="text-[10px] text-gray-400 whitespace-nowrap">
                                {new Date(chat.lastUpdate).toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' })}
                              </span>
                            </div>
                            <p className="text-xs text-gray-500 truncate">
                              {chat.messages[chat.messages.length - 1]?.text}
                            </p>
                            {chat.status === 'pending' && (
                              <span className="mt-2 inline-block w-2 h-2 bg-red-500 rounded-full animate-pulse"></span>
                            )}
                            {chat.status === 'closed' && (
                              <span className="mt-2 inline-block px-2 py-0.5 bg-gray-100 text-gray-500 text-[9px] font-bold rounded uppercase">Finalizado</span>
                            )}
                          </button>
                        ))
                      )}
                    </div>
                  </div>

                  {/* Chat Area */}
                  <div className="flex-1 bg-white rounded-2xl shadow-sm border border-gray-100 flex flex-col overflow-hidden">
                    {selectedChat ? (
                      <>
                        {/* Chat Header */}
                        <div className="p-4 border-b border-gray-100 bg-gray-50/50 flex justify-between items-center">
                          <div>
                            <h3 className="font-bold text-gray-900">{selectedChat.customerName}</h3>
                            <p className="text-xs text-gray-500">{selectedChat.customerEmail || 'Visitante'}</p>
                          </div>
                          <div className="flex items-center gap-3">
                            <span className={`px-2 py-1 rounded-full text-[10px] font-bold uppercase ${
                              selectedChat.status === 'pending' ? 'bg-red-100 text-red-600' : 
                              selectedChat.status === 'closed' ? 'bg-gray-100 text-gray-600' :
                              'bg-green-100 text-green-600'
                            }`}>
                              {selectedChat.status === 'pending' ? 'Aguardando' : 
                               selectedChat.status === 'closed' ? 'Finalizado' : 
                               'Respondido'}
                            </span>
                            
                            {selectedChat.status !== 'closed' && (
                              <button
                                onClick={() => handleCloseChat(selectedChat.customerId)}
                                className="flex items-center gap-1.5 text-[10px] font-bold text-success-600 hover:text-success-700 bg-success-50 hover:bg-success-100 px-3 py-1.5 rounded-lg transition-all border border-success-200"
                                title="Finalizar Atendimento"
                              >
                                <FiCheckCircle size={14} />
                                FINALIZAR
                              </button>
                            )}
                          </div>
                        </div>

                        {/* Messages */}
                        <div className="flex-1 overflow-y-auto p-6 space-y-4 bg-gray-50/30">
                          {/* Admin Security Warning */}
                          <div className="bg-amber-50 border border-amber-200 rounded-xl p-4 flex gap-3 items-start mb-6">
                            <FiAlertTriangle className="text-amber-600 shrink-0 mt-0.5" size={20} />
                            <div>
                              <p className="text-xs text-amber-900 font-bold uppercase tracking-wider mb-1">Diretriz de Segurança para Staff:</p>
                              <p className="text-xs text-amber-800 leading-relaxed">
                                É terminantemente proibido solicitar pagamentos, transferências PIX ou dados sensíveis (como senhas) através deste chat. Oriente sempre o cliente a usar os fluxos oficiais de pagamento do site.
                              </p>
                            </div>
                          </div>

                          {selectedChat.messages.map((msg) => (
                            <div key={msg.id} className={`flex ${msg.isStaff ? 'justify-end' : 'justify-start'}`}>
                              <div className={`max-w-[70%] p-3 rounded-2xl shadow-sm relative group ${
                                msg.isStaff 
                                  ? 'bg-primary-600 text-white rounded-tr-none' 
                                  : 'bg-white text-gray-800 rounded-tl-none border border-gray-100'
                              }`}>
                                <p className={`text-sm ${msg.isDeleted ? 'italic opacity-60' : ''}`}>{msg.text}</p>
                                
                                {/* Botão de Apagar para Staff (pode apagar qualquer mensagem não apagada) */}
                                {!msg.isDeleted && !msg.id.startsWith('msg_cust_close') && !msg.id.startsWith('msg_close') && (
                                  <button
                                    onClick={() => handleDeleteMessage(selectedChat.customerId, msg.id)}
                                    className={`absolute top-1/2 -translate-y-1/2 p-1.5 opacity-0 group-hover:opacity-100 transition-all ${
                                      msg.isStaff ? '-left-10 text-gray-400 hover:text-red-500' : '-right-10 text-gray-400 hover:text-red-500'
                                    }`}
                                    title="Apagar mensagem"
                                  >
                                    <FiTrash2 size={14} />
                                  </button>
                                )}

                                <span className={`text-[9px] mt-1 block ${msg.isStaff ? 'text-primary-100' : 'text-gray-400'}`}>
                                  {new Date(msg.timestamp).toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' })}
                                </span>
                              </div>
                            </div>
                          ))}
                        </div>

                        {/* Reply Input */}
                        <form onSubmit={handleReply} className="p-4 border-t border-gray-100 flex gap-2">
                          <input
                            type="text"
                            value={replyText}
                            onChange={(e) => setReplyText(e.target.value)}
                            placeholder="Digite sua resposta..."
                            className="flex-1 bg-gray-50 border border-gray-200 rounded-xl px-4 py-2 text-sm outline-none focus:ring-2 focus:ring-primary-500"
                          />
                          <button
                            type="submit"
                            disabled={!replyText.trim()}
                            className="bg-primary-600 hover:bg-primary-700 text-white p-2 rounded-xl transition-all disabled:opacity-50"
                          >
                            <FiSend className="w-5 h-5" />
                          </button>
                        </form>
                      </>
                    ) : (
                      <div className="flex-1 flex flex-col items-center justify-center text-gray-400 p-8">
                        <div className="w-20 h-20 bg-gray-50 rounded-full flex items-center justify-center mb-4">
                          <FiMessageSquare className="w-10 h-10 opacity-20" />
                        </div>
                        <h3 className="font-bold text-gray-600">Selecione uma conversa</h3>
                        <p className="text-sm max-w-xs text-center mt-2">
                          Escolha um cliente ao lado para iniciar o atendimento em tempo real.
                        </p>
                      </div>
                    )}
                  </div>
                </div>
              </motion.div>
            )}

            {activeTab === 'dashboard' && (
              <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
              >
                <h1 className="text-2xl font-bold text-gray-900 mb-8">Dashboard</h1>
                
                <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
                  <div className="card flex items-center gap-4">
                    <div className="w-12 h-12 bg-primary-100 text-primary-600 rounded-xl flex items-center justify-center">
                      <FiUsers size={24} />
                    </div>
                    <div>
                      <p className="text-sm text-gray-500">Total de Leads</p>
                      <h3 className="text-2xl font-bold text-gray-900">{stats.totalLeads}</h3>
                    </div>
                  </div>
                  
                  <div className="card flex items-center gap-4">
                    <div className="w-12 h-12 bg-success-100 text-success-600 rounded-xl flex items-center justify-center">
                      <FiShoppingCart size={24} />
                    </div>
                    <div>
                      <p className="text-sm text-gray-500">Vendas Aprovadas</p>
                      <h3 className="text-2xl font-bold text-gray-900">{stats.totalSales}</h3>
                    </div>
                  </div>

                  <div className="card flex items-center gap-4">
                    <div className="w-12 h-12 bg-blue-100 text-blue-600 rounded-xl flex items-center justify-center">
                      <FiBarChart2 size={24} />
                    </div>
                    <div>
                      <p className="text-sm text-gray-500">Novos Usuários</p>
                      <h3 className="text-2xl font-bold text-gray-900">{stats.totalUsers}</h3>
                    </div>
                  </div>
                </div>

                {/* Recent Leads */}
                <div className="card">
                  <h2 className="text-xl font-semibold mb-4">Leads Recentes</h2>
                  <div className="overflow-x-auto">
                    <table className="w-full">
                      <thead>
                        <tr className="border-b border-gray-200">
                          <th className="text-left py-3 px-4 font-semibold text-gray-600">CPF</th>
                          <th className="text-left py-3 px-4 font-semibold text-gray-600">Score</th>
                          <th className="text-left py-3 px-4 font-semibold text-gray-600">Status</th>
                          <th className="text-left py-3 px-4 font-semibold text-gray-600">Dívida</th>
                          <th className="text-left py-3 px-4 font-semibold text-gray-600">Data</th>
                        </tr>
                      </thead>
                      <tbody>
                        {leads.map((lead) => (
                          <tr key={lead.id} className="border-b border-gray-100 hover:bg-gray-50">
                            <td className="py-3 px-4">{formatCPF(lead.cpf)}</td>
                            <td className="py-3 px-4 font-medium">{lead.score}</td>
                            <td className="py-3 px-4">
                              <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                                lead.has_negative 
                                  ? 'bg-danger-100 text-danger-600' 
                                  : 'bg-success-100 text-success-600'
                              }`}>
                                {lead.has_negative ? 'Negativado' : 'Limpo'}
                              </span>
                            </td>
                            <td className="py-3 px-4">{formatCurrency(lead.total_debt)}</td>
                            <td className="py-3 px-4 text-gray-500 text-sm">{formatDate(lead.created_at)}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              </motion.div>
            )}

            {activeTab === 'leads' && (
              <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
              >
                <h1 className="text-2xl font-bold text-gray-900 mb-8">Todos os Leads</h1>
                <div className="card">
                  <div className="overflow-x-auto">
                    <table className="w-full">
                      <thead>
                        <tr className="border-b border-gray-200">
                          <th className="text-left py-3 px-4 font-semibold text-gray-600">ID</th>
                          <th className="text-left py-3 px-4 font-semibold text-gray-600">CPF</th>
                          <th className="text-left py-3 px-4 font-semibold text-gray-600">Score</th>
                          <th className="text-left py-3 px-4 font-semibold text-gray-600">Status</th>
                          <th className="text-left py-3 px-4 font-semibold text-gray-600">Dívida Total</th>
                          <th className="text-left py-3 px-4 font-semibold text-gray-600">Data</th>
                        </tr>
                      </thead>
                      <tbody>
                        {leads.map((lead) => (
                          <tr key={lead.id} className="border-b border-gray-100 hover:bg-gray-50">
                            <td className="py-3 px-4 text-gray-500">#{lead.id}</td>
                            <td className="py-3 px-4">{formatCPF(lead.cpf)}</td>
                            <td className="py-3 px-4 font-medium">{lead.score}</td>
                            <td className="py-3 px-4">
                              <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                                lead.has_negative 
                                  ? 'bg-danger-100 text-danger-600' 
                                  : 'bg-success-100 text-success-600'
                              }`}>
                                {lead.has_negative ? 'Negativado' : 'Limpo'}
                              </span>
                            </td>
                            <td className="py-3 px-4">{formatCurrency(lead.total_debt)}</td>
                            <td className="py-3 px-4 text-gray-500 text-sm">{formatDate(lead.created_at)}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              </motion.div>
            )}

            {activeTab === 'visits' && (
              <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
              >
                <div className="flex justify-between items-center mb-8">
                  <h1 className="text-2xl font-bold text-gray-900">Visitas e Analytics</h1>
                  <div className="flex items-center gap-3">
                    <FiFilter className="text-gray-400" />
                    <select
                      value={visitsPeriod}
                      onChange={(e) => setVisitsPeriod(e.target.value)}
                      className="bg-white border border-gray-200 rounded-xl px-4 py-2 text-sm outline-none focus:ring-2 focus:ring-primary-500"
                    >
                      <option value="all">Todo o Período</option>
                      <option value="today">Hoje</option>
                      <option value="7days">Últimos 7 Dias</option>
                      <option value="30days">Últimos 30 Dias</option>
                    </select>
                  </div>
                </div>
                
                {/* Stats Cards */}
                <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
                  <div className="card flex items-center gap-4">
                    <div className="w-12 h-12 bg-primary-100 text-primary-600 rounded-xl flex items-center justify-center">
                      <FiActivity size={24} />
                    </div>
                    <div>
                      <p className="text-sm text-gray-500">Total de Visitas</p>
                      <h3 className="text-2xl font-bold text-gray-900">{visitsData.stats.totalVisits || 0}</h3>
                    </div>
                  </div>
                  
                  <div className="card flex items-center gap-4">
                    <div className="w-12 h-12 bg-blue-100 text-blue-600 rounded-xl flex items-center justify-center">
                      <FiUsers size={24} />
                    </div>
                    <div>
                      <p className="text-sm text-gray-500">Visitas Únicas</p>
                      <h3 className="text-2xl font-bold text-gray-900">{visitsData.stats.uniqueVisits || 0}</h3>
                    </div>
                  </div>

                  <div className="card flex items-center gap-4">
                    <div className="w-12 h-12 bg-success-100 text-success-600 rounded-xl flex items-center justify-center">
                      <FiCheckCircle size={24} />
                    </div>
                    <div>
                      <p className="text-sm text-gray-500">Usuários Logados</p>
                      <h3 className="text-2xl font-bold text-gray-900">{visitsData.stats.loggedInVisits || 0}</h3>
                    </div>
                  </div>
                </div>

                {/* Visits Table */}
                <div className="card">
                  <h2 className="text-xl font-semibold mb-4">Histórico de Acessos</h2>
                  <div className="overflow-x-auto">
                    <table className="w-full">
                      <thead>
                        <tr className="border-b border-gray-200">
                          <th className="text-left py-3 px-4 font-semibold text-gray-600">Data/Hora</th>
                          <th className="text-left py-3 px-4 font-semibold text-gray-600">IP</th>
                          <th className="text-left py-3 px-4 font-semibold text-gray-600">Página</th>
                          <th className="text-left py-3 px-4 font-semibold text-gray-600">Tipo</th>
                          <th className="text-left py-3 px-4 font-semibold text-gray-600">Usuário</th>
                        </tr>
                      </thead>
                      <tbody>
                        {visitsData.visits && visitsData.visits.length > 0 ? (
                          visitsData.visits.map((visit) => (
                            <tr key={visit.id} className="border-b border-gray-100 hover:bg-gray-50">
                              <td className="py-3 px-4 text-gray-500 text-sm">{formatDate(visit.created_at)}</td>
                              <td className="py-3 px-4 font-mono text-xs text-gray-600">{visit.ip}</td>
                              <td className="py-3 px-4 text-gray-700">{visit.path || '/'}</td>
                              <td className="py-3 px-4">
                                <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                                  visit.is_logged_in 
                                    ? 'bg-primary-100 text-primary-600' 
                                    : 'bg-gray-100 text-gray-600'
                                }`}>
                                  {visit.is_logged_in ? 'Logado' : 'Visitante'}
                                </span>
                              </td>
                              <td className="py-3 px-4 text-gray-600 text-sm">
                                {visit.users?.email || visit.users?.name || '--'}
                              </td>
                            </tr>
                          ))
                        ) : (
                          <tr>
                            <td colSpan="5" className="py-8 text-center text-gray-500">
                              Nenhuma visita registrada ainda.
                            </td>
                          </tr>
                        )}
                      </tbody>
                    </table>
                  </div>
                </div>
              </motion.div>
            )}

            {activeTab === 'security' && (
              <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
              >
                <h1 className="text-2xl font-bold text-gray-900 mb-8">Segurança da Conta</h1>
                <div className="card">
                  <div className="flex items-center justify-between mb-6">
                    <div>
                      <h3 className="text-lg font-semibold text-gray-900 flex items-center gap-2">
                        <FiShield size={20} />
                        Autenticação de Dois Fatores (2FA)
                      </h3>
                      <p className="text-gray-600 mt-1">
                        Adicione uma camada extra de segurança usando o Google Authenticator
                      </p>
                    </div>
                    {twoFAEnabled ? (
                      <span className="px-4 py-2 bg-success-100 text-success-600 rounded-full font-medium flex items-center gap-2">
                        <FiCheckCircle />
                        Ativo
                      </span>
                    ) : (
                      <button
                        onClick={handleSetup2FA}
                        className="btn-primary"
                      >
                        Configurar 2FA
                      </button>
                    )}
                  </div>

                  {/* 2FA Setup Modal */}
                  {show2FASetup && (
                    <div className="border-t border-gray-100 pt-6">
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                        <div className="text-center">
                          <h4 className="font-semibold text-gray-900 mb-4">1. Escaneie o QR Code</h4>
                          <div className="inline-block bg-white p-4 rounded-lg border border-gray-200">
                            <img src={qrCodeUrl} alt="QR Code para Google Authenticator" />
                          </div>
                          <p className="text-sm text-gray-500 mt-3">
                            Abra o Google Authenticator e escaneie este QR Code
                          </p>
                        </div>
                        <div>
                          <h4 className="font-semibold text-gray-900 mb-4">2. Insira o Código</h4>
                          <div className="mb-4">
                            <p className="text-sm text-gray-600 mb-2">
                              Ou insira manualmente o secret: <span className="font-mono bg-gray-100 px-2 py-1 rounded">{secret}</span>
                            </p>
                          </div>
                          <div className="mb-4">
                            <label className="block text-sm font-medium text-gray-700 mb-1">Código de Verificação</label>
                            <input
                              type="text"
                              value={totpCode}
                              onChange={(e) => setTotpCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                              className="input-field text-center text-2xl tracking-widest font-mono"
                              placeholder="000000"
                              maxLength={6}
                              autoFocus
                            />
                          </div>
                          <div className="flex gap-3">
                            <button
                              onClick={handleVerify2FA}
                              disabled={totpCode.length < 6}
                              className="btn-primary flex-1"
                            >
                              Verificar e Ativar
                            </button>
                            <button
                              onClick={() => setShow2FASetup(false)}
                              className="btn-secondary"
                            >
                              Cancelar
                            </button>
                          </div>
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              </motion.div>
            )}


          </main>
        </div>
      </div>
    </>
  );
};

export default Admin;
